import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  SimpleGrid,
  useColorModeValue,
  Center,
  Input,
  Select,
  FormControl,
  FormLabel,
  Collapse,
  useToast,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaFilter,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaMagic,
  FaUsers,
  FaPlus,
  FaExclamationTriangle,
  FaLock,
  FaCopy,
} from 'react-icons/fa';
import { deleteDeck } from '../../utils/deckApi';
import CreateDeckModal from './CreateDeckModal';
import { checkBannedCards } from '../../constants/gameChangersBanlist';

export default function DeckList({
  decks,
  onDeckDeleted,
  onDeckUpdated,
  onDeckCreated,
  userData,
  eventsData = [],
  isUserDecks = false,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFormato, setSelectedFormato] = useState('');
  const [selectedBracket, setSelectedBracket] = useState('');
  const [selectedColors, setSelectedColors] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDeck, setEditingDeck] = useState(null);
  const [deletingDeckId, setDeletingDeckId] = useState(null);

  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  // Formati disponibili (deve corrispondere ai valori di Strapi)
  const formati = [
    'Commander Tradizionale (EDH)',
    'Duel Commander',
    'Pauper',
    'Standard',
    'Historic',
    'Legacy',
    'Altro',
  ];

  // Bracket tiers (deve corrispondere ai valori di Strapi)
  const brackets = [
    'Bracket 1',
    'Bracket 2',
    'Bracket 3',
    'Bracket 4',
    'Bracket 5',
  ];

  // Colori Magic per filtri
  const colorOptions = [
    { value: 'W', label: 'Bianco (W)', color: 'yellow.100' },
    { value: 'U', label: 'Blu (U)', color: 'blue.100' },
    { value: 'B', label: 'Nero (B)', color: 'gray.100' },
    { value: 'R', label: 'Rosso (R)', color: 'red.100' },
    { value: 'G', label: 'Verde (G)', color: 'green.100' },
    { value: 'C', label: 'Incolore (C)', color: 'gray.200' },
  ];

  // Funzione per verificare se l'utente è il proprietario del mazzo
  const isOwner = (deck) => {
    if (!userData) return false;

    // Confronta direttamente gli ID (entrambi sono numeri)
    return userData.id === deck.user?.id;
  };

  // Funzione per verificare se un mazzo è collegato a un evento futuro
  const isDeckLinkedToFutureEvent = (deck) => {
    if (!eventsData || eventsData.length === 0) return false;

    const deckId = deck.documentId || deck.id;
    if (!deckId) return false;

    const now = new Date();

    // Controlla tutti gli eventi futuri
    return eventsData.some((event) => {
      // Verifica se l'evento è futuro
      const eventDate = new Date(event.Data);
      if (eventDate <= now) return false;

      // Verifica se l'evento ha ordini con questo deck-documentId
      if (!event.orders || !Array.isArray(event.orders)) return false;

      return event.orders.some((order) => {
        // Controlla nel metadata principale
        if (order.metadata?.['deck-documentId'] === deckId) return true;

        // Controlla nei prodotti del metadata
        if (order.metadata?.products) {
          return order.metadata.products.some(
            (product) => product['deck-documentId'] === deckId
          );
        }

        return false;
      });
    });
  };

  // Funzione per renderizzare i badge di warning per le carte bandite
  const renderBanlistWarning = (deck) => {
    const { hasBanned } = checkBannedCards(deck);

    if (!hasBanned) return null;

    return (
      <Badge
        colorScheme="orange"
        variant="solid"
        size="sm"
        leftIcon={<Icon as={FaExclamationTriangle} />}
        title="Carte bandite nel formato Commander"
        display="inline-flex"
        width="fit-content"
      >
        TMC
      </Badge>
    );
  };

  // Funzione per ottenere l'immagine della prima carta del mazzo
  const getFirstCardImage = (deck) => {
    if (!deck.deck || !Array.isArray(deck.deck) || deck.deck.length === 0) {
      return null;
    }

    // Cerca la prima carta con un'immagine
    const firstCardWithImage = deck.deck.find(
      (card) =>
        card.image_uris &&
        (card.image_uris.art_crop ||
          card.image_uris.normal ||
          card.image_uris.small)
    );

    if (firstCardWithImage) {
      // Priorità: art_crop > normal > small
      return (
        firstCardWithImage.image_uris.art_crop ||
        firstCardWithImage.image_uris.normal ||
        firstCardWithImage.image_uris.small
      );
    }

    return null;
  };

  // Filtra i mazzi
  const filteredDecks = decks.filter((deck) => {
    // Controlla se l'utente è admin o helper
    const isAdminOrHelper =
      userData?.type === 'admin' || userData?.type === 'helper';

    // Escludi i mazzi privati (isPublic = false) se NON stiamo visualizzando i mazzi dell'utente
    // MA permettere a admin e helper di vedere tutti i mazzi
    if (!isUserDecks && deck.isPublic === false && !isAdminOrHelper) {
      return false;
    }

    // Escludi i mazzi collegati a eventi futuri solo se NON stiamo visualizzando i mazzi dell'utente
    // MA permettere a admin e helper di vedere tutti i mazzi
    if (!isUserDecks && isDeckLinkedToFutureEvent(deck) && !isAdminOrHelper) {
      return false;
    }

    // Filtra per query di ricerca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const deckName = deck.nomeMazzo?.toLowerCase() || '';
      const description = deck.description?.toLowerCase() || '';

      // Cerca nel nome utente in tutti i possibili campi
      const userUsername =
        deck.user?.username?.toLowerCase() ||
        deck.socio?.username?.toLowerCase() ||
        '';
      const userFirstName =
        deck.user?.firstName?.toLowerCase() ||
        deck.socio?.firstName?.toLowerCase() ||
        '';
      const userLastName =
        deck.user?.lastName?.toLowerCase() ||
        deck.socio?.lastName?.toLowerCase() ||
        '';

      // Combina nome e cognome per ricerca più flessibile
      const fullName = `${userFirstName} ${userLastName}`.trim();

      if (
        !deckName.includes(query) &&
        !description.includes(query) &&
        !userUsername.includes(query) &&
        !userFirstName.includes(query) &&
        !userLastName.includes(query) &&
        !fullName.includes(query)
      ) {
        return false;
      }
    }

    // Filtra per formato
    if (selectedFormato && deck.formato !== selectedFormato) {
      return false;
    }

    // Filtra per bracket
    if (selectedBracket && deck.bracket !== selectedBracket) {
      return false;
    }

    // Filtra per colori
    if (selectedColors && deck.colors) {
      const deckColors = deck.colors.replace(/[{}]/g, '');
      if (!deckColors.includes(selectedColors)) {
        return false;
      }
    }

    return true;
  });

  // Funzione per eliminare un mazzo
  const handleDeleteDeck = async (deck) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo mazzo?')) {
      return;
    }

    // Verifica che l'utente sia autorizzato a eliminare questo mazzo
    if (!isOwner(deck)) {
      toast({
        title: 'Non autorizzato',
        description: 'Non puoi eliminare questo mazzo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Strapi usa documentId come identificatore principale
    const deckId = deck.documentId || deck.id;
    if (!deckId) {
      console.error('ID del mazzo non trovato:', deck);
      toast({
        title: 'Errore',
        description: 'ID del mazzo non valido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Debug: log per verificare l'autorizzazione
    console.log('Delete deck authorization:', {
      userUsername: userData?.username,
      deckUserUsername: deck.user?.username,
      isOwner: isOwner(deck),
      deckId,
      deck,
    });

    setDeletingDeckId(deckId);

    try {
      await deleteDeck(deckId);

      // Notifica il componente padre per aggiornare la lista
      onDeckDeleted(deckId);

      toast({
        title: 'Mazzo eliminato',
        description: 'Il mazzo è stato eliminato con successo',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || "Errore nell'eliminazione del mazzo",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeletingDeckId(null);
    }
  };

  // Funzione per navigare ai dettagli di un mazzo
  const handleViewDeck = (deck) => {
    // Debug: log della struttura del deck per capire quale campo ID usare
    console.log('Struttura deck per navigazione:', {
      id: deck.id,
      documentId: deck.documentId,
      fullDeck: deck,
    });

    // Strapi usa documentId come identificatore principale
    const deckId = deck.documentId || deck.id;
    if (!deckId) {
      console.error('ID del mazzo non trovato:', deck);
      toast({
        title: 'Errore',
        description: 'ID del mazzo non valido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    console.log('ID del mazzo utilizzato per la navigazione:', deckId);
    navigate(`/decks/${deckId}`);
  };

  // Funzione per aprire il modal di modifica di un mazzo
  const handleEditDeck = (deck) => {
    // Verifica che l'utente sia autorizzato a modificare questo mazzo
    if (!isOwner(deck)) {
      toast({
        title: 'Non autorizzato',
        description: 'Non puoi modificare questo mazzo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Debug: log per verificare l'autorizzazione
    console.log('Edit deck authorization:', {
      userUsername: userData?.username,
      deckUserUsername: deck.user?.username,
      isOwner: isOwner(deck),
      deck,
    });

    setEditingDeck(deck);
    setIsCreateModalOpen(true);
  };

  // Funzione per gestire l'aggiornamento di un mazzo
  const handleDeckUpdated = (updatedDeck) => {
    // Aggiorna la lista dei deck con il deck modificato
    const updatedDecks = decks.map((deck) => {
      const deckId = deck.documentId || deck.id;
      const updatedDeckId = updatedDeck.documentId || updatedDeck.id;
      return deckId === updatedDeckId ? updatedDeck : deck;
    });

    // Aggiorna la lista dei deck (questo richiede che il componente padre passi una funzione onDeckUpdated)
    if (onDeckUpdated) {
      onDeckUpdated(updatedDecks);
    }

    setEditingDeck(null);
    setIsCreateModalOpen(false);

    toast({
      title: 'Mazzo aggiornato',
      description: 'Il mazzo è stato aggiornato con successo',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Funzione per gestire la creazione di un nuovo mazzo
  const handleDeckCreated = (newDeck) => {
    // Aggiungi il nuovo deck alla lista
    if (onDeckCreated) {
      onDeckCreated(newDeck);
    }

    setIsCreateModalOpen(false);

    toast({
      title: 'Mazzo creato',
      description: 'Il mazzo è stato creato con successo',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Funzione per chiudere il modal
  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingDeck(null);
  };

  // Funzione per ottenere il colore del badge del formato
  const getFormatoColor = (formato) => {
    const colorMap = {
      'Commander Tradizionale (EDH)': 'orange',
      'Duel Commander': 'red',
      Pauper: 'yellow',
      Standard: 'green',
      Historic: 'blue',
      Legacy: 'purple',
      Altro: 'gray',
    };
    return colorMap[formato] || 'gray';
  };

  // Funzione per ottenere il colore del badge del bracket
  const getBracketColor = (bracket) => {
    const colorMap = {
      'Bracket 1': 'red',
      'Bracket 2': 'orange',
      'Bracket 3': 'green',
      'Bracket 4': 'blue',
      'Bracket 5': 'purple',
    };
    return colorMap[bracket] || 'gray';
  };

  // Funzione per renderizzare i colori del mazzo
  const renderDeckColors = (colors) => {
    if (!colors) return null;

    const colorSymbols = colors.replace(/[{}]/g, '');
    return (
      <HStack spacing={1}>
        {colorSymbols.split('').map((color) => {
          const colorInfo = colorOptions.find((c) => c.value === color);
          if (!colorInfo) return null;

          return (
            <Badge
              key={`${color}-${colorInfo.label}`}
              colorScheme={colorInfo.color.split('.')[0]}
              variant="solid"
              size="sm"
              px={2}
              py={1}
            >
              {color}
            </Badge>
          );
        })}
      </HStack>
    );
  };

  // Funzione per formattare il mazzo nel formato esportazione
  const formatDeckForExport = (deck) => {
    if (!deck.deck || !Array.isArray(deck.deck) || deck.deck.length === 0) {
      return '';
    }

    const lines = deck.deck
      .map((card) => {
        const quantity = card.quantity || 1;
        const name = card.nome || card.name || '';
        const set = card.set || '';
        const collectorNumber = card.collector_number || '';

        if (!name) return '';

        // Formato: quantità Nome Carta (SET) numero
        let line = `${quantity} ${name}`;

        if (set) {
          line += ` (${set.toUpperCase()})`;
        }

        if (collectorNumber) {
          line += ` ${collectorNumber}`;
        }

        return line;
      })
      .filter((line) => line !== ''); // Rimuovi righe vuote

    return lines.join('\n\n'); // Doppio a-capo tra le carte come nell'esempio
  };

  // Funzione per copiare il mazzo negli appunti
  const handleCopyDeck = async (deck) => {
    try {
      const formattedDeck = formatDeckForExport(deck);

      if (!formattedDeck) {
        toast({
          title: 'Errore',
          description: 'Il mazzo non contiene carte valide',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      await navigator.clipboard.writeText(formattedDeck);

      toast({
        title: 'Mazzo copiato',
        description: 'Il mazzo è stato copiato negli appunti',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Errore durante la copia:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile copiare il mazzo negli appunti',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (decks.length === 0) {
    return (
      <Box>
        <Center py={20}>
          <VStack spacing={4}>
            <Icon as={FaMagic} size="xl" color="gray.400" />
            <Text color="gray.600" fontSize="lg">
              Nessun mazzo trovato
            </Text>
            <Text color="gray.500" fontSize="md">
              Inizia creando il tuo primo mazzo Magic!
            </Text>
            <Button
              colorScheme="purple"
              leftIcon={<FaPlus />}
              onClick={() => setIsCreateModalOpen(true)}
              size="lg"
            >
              Crea il Primo Mazzo
            </Button>
          </VStack>
        </Center>

        {/* Modal per creazione mazzo */}
        <CreateDeckModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          onDeckCreated={handleDeckCreated}
          onDeckUpdated={handleDeckUpdated}
          editingDeck={editingDeck}
          userData={userData}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filtri e ricerca */}
      <Box bg={cardBg} boxShadow={cardShadow} borderRadius="lg" p={4} mb={6}>
        <VStack spacing={4} align="stretch">
          {/* Barra di ricerca */}
          <FormControl>
            <FormLabel>🔍 Cerca Mazzi</FormLabel>
            <Input
              placeholder="Cerca per nome mazzo, descrizione o utente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<FaSearch />}
            />
          </FormControl>

          {/* Pulsanti azioni */}
          <HStack spacing={3} justify="space-between">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              rightIcon={showFilters ? <FaChevronUp /> : <FaChevronDown />}
              leftIcon={<FaFilter />}
              size="sm"
            >
              {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri Avanzati'}
            </Button>

            <Button
              colorScheme="purple"
              leftIcon={<FaPlus />}
              onClick={() => setIsCreateModalOpen(true)}
              size="sm"
            >
              Crea Nuovo Mazzo
            </Button>
          </HStack>

          {/* Filtri avanzati */}
          <Collapse in={showFilters}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel>Formato</FormLabel>
                <Select
                  placeholder="Tutti i formati"
                  value={selectedFormato}
                  onChange={(e) => setSelectedFormato(e.target.value)}
                >
                  {formati.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Bracket</FormLabel>
                <Select
                  placeholder="Tutti i bracket"
                  value={selectedBracket}
                  onChange={(e) => setSelectedBracket(e.target.value)}
                >
                  {brackets.map((bracket) => (
                    <option key={bracket} value={bracket}>
                      {bracket}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Colore</FormLabel>
                <Select
                  placeholder="Tutti i colori"
                  value={selectedColors}
                  onChange={(e) => setSelectedColors(e.target.value)}
                >
                  {colorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            {/* Pulsanti per resettare i filtri */}
            <HStack justify="center" pt={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedFormato('');
                  setSelectedBracket('');
                  setSelectedColors('');
                  setSearchQuery('');
                }}
              >
                Reset Filtri
              </Button>
            </HStack>
          </Collapse>
        </VStack>
      </Box>

      {/* Lista mazzi */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filteredDecks.map((deck) => {
          // Debug: log dei dati del mazzo
          console.log('Dati mazzo:', {
            id: deck.id,
            nomeMazzo: deck.nomeMazzo,
            deck: deck.deck,
            user: deck.user,
            socio: deck.socio,
            cardCount: deck.cardCount,
          });

          return (
            <Box
              key={deck.id}
              bg={cardBg}
              boxShadow={cardShadow}
              borderRadius="lg"
              overflow="hidden"
              transition="all 0.2s"
              position="relative"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
              }}
            >
              {/* Layout principale con immagine a destra */}
              <HStack spacing={0} align="stretch">
                {/* Sezione sinistra con informazioni del mazzo */}
                <Box flex={1} p={4}>
                  <VStack spacing={4} align="stretch">
                    {/* Header con nome mazzo */}
                    <HStack justify="space-between" align="start">
                      <Text
                        fontWeight="bold"
                        fontSize="lg"
                        color="blue.600"
                        noOfLines={2}
                        flex={1}
                      >
                        {deck.nomeMazzo}
                      </Text>
                    </HStack>

                    {/* Badge formato e bracket */}
                    <HStack spacing={2} wrap="wrap">
                      {deck.formato && (
                        <Badge
                          colorScheme={getFormatoColor(deck.formato)}
                          variant="solid"
                          size="sm"
                        >
                          {deck.formato}
                        </Badge>
                      )}

                      {deck.bracket && (
                        <Badge
                          colorScheme={getBracketColor(deck.bracket)}
                          variant="outline"
                          size="sm"
                        >
                          {deck.bracket}
                        </Badge>
                      )}

                      {deck.isPublic === false && (
                        <Badge
                          colorScheme="gray"
                          variant="solid"
                          size="sm"
                          leftIcon={<Icon as={FaLock} />}
                        >
                          Privato
                        </Badge>
                      )}
                    </HStack>

                    {/* Badge warning per carte bandite Game Changers */}
                    {renderBanlistWarning(deck)}

                    {/* Colori del mazzo */}
                    {deck.colors && (
                      <Box>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          mb={2}
                          fontWeight="medium"
                        >
                          Colori:
                        </Text>
                        {renderDeckColors(deck.colors)}
                      </Box>
                    )}

                    {/* Descrizione */}
                    {deck.description && (
                      <Box>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          noOfLines={3}
                          lineHeight="1.4"
                        >
                          {deck.description}
                        </Text>
                      </Box>
                    )}

                    {/* Statistiche del mazzo */}
                    <HStack
                      justify="space-between"
                      fontSize="sm"
                      color="gray.500"
                      pt={2}
                    >
                      <HStack spacing={1}>
                        <Icon as={FaMagic} color="gray.500" />
                        <Text>
                          {deck.cardCount ||
                            (deck.deck && Array.isArray(deck.deck)
                              ? deck.deck.reduce(
                                  (total, card) => total + (card.quantity || 1),
                                  0
                                )
                              : 0)}{' '}
                          carte
                        </Text>
                      </HStack>

                      <HStack spacing={1}>
                        <Icon as={FaUsers} color="gray.500" />
                        <Text>
                          {deck.user?.firstName && deck.user?.lastName
                            ? `${deck.user.firstName} ${deck.user.lastName}`
                            : deck.user?.data?.firstName &&
                                deck.user?.data?.lastName
                              ? `${deck.user.data.firstName} ${deck.user.data.lastName}`
                              : deck.socio?.firstName && deck.socio?.lastName
                                ? `${deck.socio.firstName} ${deck.socio.lastName}`
                                : deck.socio?.data?.firstName &&
                                    deck.socio?.data?.lastName
                                  ? `${deck.socio.data.firstName} ${deck.socio.data.lastName}`
                                  : deck.user?.username ||
                                    deck.user?.data?.username ||
                                    deck.socio?.username ||
                                    deck.socio?.data?.username ||
                                    'Utente'}
                        </Text>
                      </HStack>
                    </HStack>

                    {/* Azioni del mazzo */}
                    <HStack spacing={3} pt={2} pb={2} flexWrap="wrap">
                      <Button
                        colorScheme="blue"
                        variant="outline"
                        size="sm"
                        leftIcon={<FaEye />}
                        onClick={() => handleViewDeck(deck)}
                        flex={1}
                        minW="100px"
                      >
                        Visualizza
                      </Button>
                    </HStack>
                  </VStack>
                </Box>

                {/* Sezione destra con immagine della carta */}
                {getFirstCardImage(deck) && (
                  <Box
                    w="120px"
                    minH="200px"
                    bgImage={`url(${getFirstCardImage(deck)})`}
                    bgSize="cover"
                    bgPosition="center"
                    bgRepeat="no-repeat"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                    flexShrink={0}
                    borderRadius="0 lg 0"
                    position="relative"
                    zIndex={1}
                  >
                    {/* Azioni del mazzo - posizionate sopra l'immagine */}
                    <HStack
                      spacing={1}
                      position="absolute"
                      top={2}
                      right={2}
                      zIndex={2}
                    >
                      <IconButton
                        aria-label="Copia mazzo negli appunti"
                        icon={<FaCopy />}
                        size="xs"
                        variant="solid"
                        colorScheme="blue"
                        onClick={() => handleCopyDeck(deck)}
                        opacity={0.8}
                        _hover={{ bg: 'blue.500', opacity: 1 }}
                      />
                      {isOwner(deck) && (
                        <>
                          <IconButton
                            aria-label="Modifica mazzo"
                            icon={<FaEdit />}
                            size="xs"
                            variant="solid"
                            colorScheme="orange"
                            onClick={() => handleEditDeck(deck)}
                            opacity={0.8}
                            _hover={{ bg: 'orange.500', opacity: 1 }}
                          />
                          <IconButton
                            aria-label="Elimina mazzo"
                            icon={<FaTrash />}
                            size="xs"
                            variant="solid"
                            colorScheme="red"
                            onClick={() => handleDeleteDeck(deck)}
                            isLoading={
                              deletingDeckId === (deck.documentId || deck.id)
                            }
                            opacity={0.8}
                            _hover={{ bg: 'red.500', opacity: 1 }}
                          />
                        </>
                      )}
                    </HStack>
                  </Box>
                )}
              </HStack>
            </Box>
          );
        })}
      </SimpleGrid>

      {/* Modal per creazione/modifica mazzo */}
      <CreateDeckModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onDeckCreated={handleDeckCreated}
        onDeckUpdated={handleDeckUpdated}
        editingDeck={editingDeck}
        userData={userData}
      />
    </Box>
  );
}
